import OneSignal from "react-onesignal";
import AppConfig from "./helpers/config";
import {ServicePostPackageTag} from "./services/NotificationService";

const fetchAndSendIds = async () => {
    try {
        const oneSignalId = OneSignal.User.onesignalId;
        const subscriptionId = OneSignal.User.PushSubscription.id;

        console.log("OneSignal ID:", oneSignalId || "Not available");
        console.log("Subscription ID:", subscriptionId || "Not available");

        if (oneSignalId && subscriptionId) {
            const params = {
                player_id: oneSignalId,
                subscription_id: subscriptionId,
                platform: "web",
                device_type: "desktop",
                device_name: navigator.userAgent,
            };

            const response = await ServicePostPackageTag(params);
            console.log({response});

            localStorage.setItem("oneSignalId", oneSignalId);
            localStorage.setItem("subscriptionId", subscriptionId);
        }
    } catch (error) {
        console.error("Error fetching OneSignal IDs (Web):", error);
    }
};

const isIOS = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator?.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// iOS only delivers Web Push to a site the user installed on the Home Screen.
const isStandalonePWA = () =>
    window.navigator.standalone === true ||
    window.matchMedia?.("(display-mode: standalone)")?.matches === true;

/**
 * Whether this device can actually end up subscribed.
 *
 * `isPushSupported()` only feature-detects the Push API, and Safari on iOS 16.4+
 * exposes it in an ordinary tab where push nevertheless cannot work. The SDK only
 * discovers that later, inside optIn(), where it rejects with "This system is
 * unable to receive push notifications." — so iOS needs the extra standalone check.
 */
export const canReceivePushNotifications = () => {
    try {
        if (!OneSignal.Notifications.isPushSupported()) return false;
        if (isIOS() && !isStandalonePWA()) return false;
        return true;
    } catch (error) {
        return false;
    }
};

export const initOneSignal = async () => {
    // init() rejects outright on a browser without Push (in-app browsers, older
    // Android WebViews). Skipping reaches the same end state without the
    // unhandled rejection that used to surface as a runtime error.
    if (!OneSignal.Notifications.isPushSupported()) {
        console.info("OneSignal: Web Push unsupported in this browser, skipping init.");
        return false;
    }

    try {
        await OneSignal.init({
            appId: AppConfig.oneSignalKey,
            notifyButton: {enable: true},
            allowLocalhostAsSecureOrigin: true,
        });
        return true;
    } catch (error) {
        console.warn("OneSignal: init failed —", error?.message || error);
        return false;
    }
};

export const requestNotificationPermission = async () => {
    if (!canReceivePushNotifications()) {
        console.info("OneSignal: this device cannot receive push, skipping opt-in.");
        return false;
    }

    try {
        // 🔔 Request permission if not granted
        if (!OneSignal.Notifications.permission) {
            const granted = await OneSignal.Notifications.requestPermission();
            // An explicit denial makes the opt-in below a guaranteed throw.
            if (granted === false) return false;
        }

        // ✅ FORCE opt-in (critical for web)
        await OneSignal.User.PushSubscription.optIn();

        // ⏳ Wait briefly for subscription to be ready
        let retries = 5;
        while (
            retries-- &&
            (!OneSignal.User.onesignalId ||
                !OneSignal.User.PushSubscription.id)
            ) {
            await new Promise((res) => setTimeout(res, 500));
        }

        // 🚀 Send IDs
        await fetchAndSendIds();
        return true;
    } catch (error) {
        // Permission denied, a blocked/unsupported system, or a private-mode
        // service-worker failure. None of these should reach the user as an error.
        console.warn("OneSignal: could not enable push —", error?.message || error);
        return false;
    }
};
